import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Paper,
  IconButton,
  Grid,
  Divider,
  useMediaQuery,
  useTheme,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import PersonIcon from "@material-ui/icons/Person";
import BusinessIcon from "@material-ui/icons/Business";
import DateRangeIcon from "@material-ui/icons/DateRange";
import MessageIcon from "@material-ui/icons/Message";
import PeopleIcon from "@material-ui/icons/People";
import PhotoIcon from "@material-ui/icons/Photo";
import DescriptionIcon from "@material-ui/icons/Description";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  dialog: {
    [theme.breakpoints.up("md")]: {
      maxWidth: 800,
    },
    width: "100%",
  },
  dialogContent: {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    height: "70vh",
    [theme.breakpoints.down("xs")]: {
      height: "80vh",
    },
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    height: "100%",
  },
  errorContainer: {
    padding: theme.spacing(2),
    backgroundColor: "#ffebee",
    color: "#c62828",
    borderRadius: theme.shape.borderRadius,
    border: "1px solid #ef9a9a",
    margin: theme.spacing(2),
  },
  statsSection: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  statCard: {
    textAlign: "center",
    padding: theme.spacing(1),
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  statLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
  },
  participantsSection: {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  participantChip: {
    margin: theme.spacing(0.5),
  },
  chatContainer: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2),
    backgroundColor: "#e5ddd5",
    backgroundImage: 'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAABmJLR0QA/wD/AP+gvaeTAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAB3RJTUUH4wEMEgQnPgfHhwAAAB1pVFh0Q29tbWVudAAAAAAAQ3JlYXRlZCB3aXRoIEdJTVBkLmUHAAAAKklEQVQ4y2P8////fwYKABMDhYAFmVNUVPSfiYmJgYGBgeH/fyIVDiQAABvgBQ5E5IfGAAAAAElFTkSuQmCC")',
  },
  messageBubble: {
    maxWidth: "75%",
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    marginBottom: theme.spacing(0.5),
    wordBreak: "break-word",
    position: "relative",
    boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
  },
  messageFromCustomer: {
    backgroundColor: "#fff",
    marginRight: "auto",
    borderTopLeftRadius: 0,
  },
  messageFromBusiness: {
    backgroundColor: "#dcf8c6",
    marginLeft: "auto",
    borderTopRightRadius: 0,
  },
  messageSender: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.25),
  },
  messageText: {
    fontSize: "0.875rem",
    lineHeight: 1.4,
  },
  messageTime: {
    fontSize: "0.65rem",
    color: theme.palette.text.secondary,
    textAlign: "right",
    marginTop: theme.spacing(0.25),
  },
  messageMedia: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    fontStyle: "italic",
  },
  hasMoreIndicator: {
    textAlign: "center",
    padding: theme.spacing(2),
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: 8,
    margin: theme.spacing(1),
  },
  dateHeader: {
    textAlign: "center",
    margin: theme.spacing(2, 0, 1),
  },
  dateChip: {
    backgroundColor: "rgba(225, 245, 254, 0.92)",
    fontSize: "0.75rem",
  },
}));

const BackupPreviewModal = ({ open, onClose, backupId, onRestore }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const chatContainerRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [messageLimit, setMessageLimit] = useState(100);

  useEffect(() => {
    if (open && backupId) {
      setMessageLimit(100);
      loadPreview(100);
    } else {
      setPreviewData(null);
      setError(null);
      setMessageLimit(100);
    }
  }, [open, backupId]);

  const loadPreview = async (limit = messageLimit) => {
    setLoading(true);
    setError(null);

    try {
      const { data } = await api.get(`/whatsapp-backups/${backupId}/preview?limit=${limit}`);
      setPreviewData(data);
    } catch (err) {
      console.error("[BackupPreviewModal] Error loading preview:", err);
      setError(err.response?.data?.error || err.message || "Failed to load preview");
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    const newLimit = messageLimit + 200;
    setLoadingMore(true);
    
    try {
      const { data } = await api.get(`/whatsapp-backups/${backupId}/preview?limit=${newLimit}`);
      setPreviewData(data);
      setMessageLimit(newLimit);
    } catch (err) {
      console.error("[BackupPreviewModal] Error loading more:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadAll = async () => {
    setLoadingMore(true);
    
    try {
      // Load all messages (set very high limit)
      const { data } = await api.get(`/whatsapp-backups/${backupId}/preview?limit=10000`);
      setPreviewData(data);
      setMessageLimit(10000);
    } catch (err) {
      console.error("[BackupPreviewModal] Error loading all:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString(i18n.language, {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  // Group messages by date for better readability
  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentDate = null;
    let currentGroup = [];

    messages.forEach((msg) => {
      const msgDate = msg.date ? formatDate(msg.date) : null;
      
      if (msgDate !== currentDate) {
        if (currentGroup.length > 0) {
          groups.push({ date: currentDate, messages: currentGroup });
        }
        currentDate = msgDate;
        currentGroup = [msg];
      } else {
        currentGroup.push(msg);
      }
    });

    if (currentGroup.length > 0) {
      groups.push({ date: currentDate, messages: currentGroup });
    }

    return groups;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <DescriptionIcon color="primary" />
            <Typography variant={isMobile ? "subtitle1" : "h6"}>
              {i18n.t("whatsappBackups.preview.title") || "Chat Preview"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        {previewData?.backup && (
          <Typography variant="caption" color="textSecondary">
            {previewData.backup.fileName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="body2" style={{ marginTop: 16 }}>
              {i18n.t("whatsappBackups.preview.loading") || "Loading preview..."}
            </Typography>
          </Box>
        ) : error ? (
          <Box className={classes.errorContainer}>
            <Typography variant="body2">
              <strong>Error:</strong> {error}
            </Typography>
          </Box>
        ) : previewData ? (
          <>
            {/* Statistics Section */}
            <Box className={classes.statsSection}>
              <Grid container spacing={1}>
                <Grid item xs={3}>
                  <Box className={classes.statCard}>
                    <MessageIcon color="primary" />
                    <Typography className={classes.statValue}>
                      {previewData.statistics.totalMessages}
                    </Typography>
                    <Typography className={classes.statLabel}>
                      {i18n.t("whatsappBackups.messages") || "Messages"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box className={classes.statCard}>
                    <PeopleIcon color="primary" />
                    <Typography className={classes.statValue}>
                      {previewData.statistics.totalContacts}
                    </Typography>
                    <Typography className={classes.statLabel}>
                      {i18n.t("whatsappBackups.contacts") || "Contacts"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box className={classes.statCard}>
                    <PhotoIcon color="primary" />
                    <Typography className={classes.statValue}>
                      {previewData.statistics.mediaCount || 0}
                    </Typography>
                    <Typography className={classes.statLabel}>
                      {i18n.t("whatsappBackups.media") || "Media"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box className={classes.statCard}>
                    <DateRangeIcon color="primary" />
                    <Typography variant="caption" style={{ fontWeight: 600 }}>
                      {previewData.statistics.dateRange?.earliest
                        ? formatDate(previewData.statistics.dateRange.earliest)
                        : "N/A"}
                    </Typography>
                    <Typography className={classes.statLabel}>
                      {i18n.t("whatsappBackups.preview.dateRange") || "Start Date"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Participants Section */}
            <Box className={classes.participantsSection}>
              <Typography variant="caption" color="textSecondary">
                {i18n.t("whatsappBackups.preview.participants") || "Participants"}:
              </Typography>
              <Box display="flex" flexWrap="wrap" mt={0.5}>
                {previewData.participants?.map((participant, idx) => (
                  <Chip
                    key={idx}
                    size="small"
                    icon={participant.isBusinessAccount ? <BusinessIcon /> : <PersonIcon />}
                    label={`${participant.name} (${participant.messageCount})`}
                    className={classes.participantChip}
                    color={participant.isBusinessAccount ? "primary" : "default"}
                    variant={participant.isBusinessAccount ? "default" : "outlined"}
                  />
                ))}
              </Box>
            </Box>

            {/* Chat Messages Section */}
            <Box className={classes.chatContainer} ref={chatContainerRef}>
              {groupMessagesByDate(previewData.messages).map((group, groupIdx) => (
                <Box key={groupIdx}>
                  {group.date && (
                    <Box className={classes.dateHeader}>
                      <Chip label={group.date} size="small" className={classes.dateChip} />
                    </Box>
                  )}
                  {group.messages.map((msg, msgIdx) => (
                    <Box
                      key={msgIdx}
                      className={`${classes.messageBubble} ${
                        msg.isFromCustomer
                          ? classes.messageFromCustomer
                          : classes.messageFromBusiness
                      }`}
                    >
                      <Typography className={classes.messageSender}>
                        {msg.sender}
                      </Typography>
                      {msg.mediaType && (
                        <Box className={classes.messageMedia}>
                          <PhotoIcon fontSize="small" />
                          <span>{msg.mediaFileName || msg.mediaType}</span>
                        </Box>
                      )}
                      <Typography className={classes.messageText}>
                        {msg.message}
                      </Typography>
                      <Typography className={classes.messageTime}>
                        {formatTime(msg.date)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))}

              {previewData.hasMore && (
                <Box className={classes.hasMoreIndicator}>
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                    {i18n.t("whatsappBackups.preview.showingMessages", {
                      shown: previewData.messages.length,
                      total: previewData.totalAvailable,
                    }) ||
                      `Showing ${previewData.messages.length} of ${previewData.totalAvailable} messages`}
                  </Typography>
                  <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={loadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <CircularProgress size={16} style={{ marginRight: 8 }} />
                      ) : null}
                      {i18n.t("whatsappBackups.preview.loadMore") || "Load 200 More"}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={loadAll}
                      disabled={loadingMore}
                    >
                      {i18n.t("whatsappBackups.preview.loadAll") || "Load All"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Box className={classes.loadingContainer}>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("whatsappBackups.preview.noData") || "No preview data available"}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{i18n.t("buttons.cancel") || "Close"}</Button>
        {previewData && onRestore && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => onRestore(backupId)}
          >
            {i18n.t("whatsappBackups.restore.button") || "Restore to Ticket"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BackupPreviewModal;


